import posthog from 'posthog-js';

export type AiUploadEventName =
  | 'ai_upload.entry_clicked'
  | 'ai_upload.job_created'
  | 'ai_upload.job_linked_created'
  | 'ai_upload.job_started_processing'
  | 'ai_upload.job_draft_ready'
  | 'ai_upload.job_published'
  | 'ai_upload.job_failed'
  | 'ai_upload.job_cancelled'
  | 'ai_upload.job_retried'
  | 'ai_upload.multi_round_started'
  | 'ai_upload.draft_editor_opened'
  | 'ai_upload.issue_clicked'
  | 'ai_upload.quota_exceeded'
  | 'ai_upload.batch_submission_started'
  | 'ai_upload.batch_submission_complete'
  | 'ai_upload.batch_cancelled_by_user'
  | 'ai_upload.module_approved'
  | 'ai_upload.lesson_approved'
  | 'ai_upload.approval_downgraded'
  | 'ai_upload.draft_fully_approved'
  | 'ai_upload.publish_attempted'
  | 'ai_upload.batch_edit_requested'
  | 'ai_upload.batch_edit_applied'
  | 'ai_upload.batch_edit_canceled'
  | 'ai_upload.batch_edit_failed'
  | 'ai_upload.phase_b_entered'
  | 'ai_upload.phase_b_completed'
  | 'ai_upload.phase_c_completed'
  | 'ai_upload.repair_requested'
  | 'ai_upload.repair_completed'
  | 'ai_upload.repair_failed'
  /** G7 — lesson review surface events */
  | 'ai_upload.lesson_review_opened'
  | 'ai_upload.lesson_review_warning_seen'
  | 'ai_upload.step_detail_viewed';

export type PageBucket = '1-10' | '11-25' | '26-50' | '50+';

export function getPageBucket(pageCount: number): PageBucket {
  if (pageCount <= 10) return '1-10';
  if (pageCount <= 25) return '11-25';
  if (pageCount <= 50) return '26-50';
  return '50+';
}

export interface AiUploadEventProperties {
  orgId?: string;
  userId?: string;
  jobId?: string;
  intent?: string;
  uploadGroupId?: string;
  fileCount?: number;
  pageBucket?: PageBucket;
  moduleCount?: number;
  lessonCount?: number;
  durationSeconds?: number;
  privacyPreset?: string;
  errorType?: string;
  cancelledAtStatus?: string;
  roundNumber?: number;
  issueType?: string;
  severity?: string;
  quotaType?: 'jobs' | 'pages';
  batchIndex?: number;
  totalBatches?: number;
  submittedBatches?: number;
  blockedBatches?: number;
  pilotEnabled?: boolean;
  approvalType?: 'module' | 'lesson';
  downgradeCause?: string;
  allApproved?: boolean;
  blockerCount?: number;
  stepsNeedingAttention?: number;
  batchEditFieldCount?: number;
  batchEditScopeModules?: number;
  batchEditScopeLessons?: number;
  batchEditFailReason?: string;
  pipelineMode?: 'coordinate_fast_path' | 'llm_structured';
  phaseADurationSeconds?: number;
  phaseBDurationSeconds?: number;
  phaseCDurationSeconds?: number;
  usedBatchEdit?: boolean;
  repairScopeType?: string;
  repairStrategy?: string;
  repairReason?: string;
  repairLessonsUpdated?: number;
  repairStepsUpdated?: number;
  repairStepsCreated?: number;
  repairStepsDeleted?: number;
  repairFailReason?: string;
  /** G7 — lesson review surface properties */
  moduleIndex?: number;
  lessonIndex?: number;
  stepIndex?: number;
  hasWarnings?: boolean;
  hasErrors?: boolean;
  hasText?: boolean;
  hasImages?: boolean;
  hasInteraction?: boolean;
  warningCount?: number;
  errorCount?: number;
}

export function trackAiUpload(
  event: AiUploadEventName,
  properties: AiUploadEventProperties = {}
): void {
  try {
    posthog.capture(event, properties);
  } catch {
    // Never let analytics failures affect the UI
  }
}
